// src/utils/cache/area-data-preloader.js
// Preloads area-specific data (packs, tags, maps) for instant navigation
// Single source of truth for area data preloading

import { LOGGING } from '/settings.js';
import { DATA_PATHS } from '../../dataPaths.js';
import { getPacksForArea } from '../spot-queries.js';
import { fetchWithCache, preloadMinimalForArea } from './index.js';
import { triggerAreaTagBatch } from './explore-tags-cache.js';
import { triggerAreaBatch } from './cards-cache.js';

const MAPS_API = '/app_core/api/sqlite/get-maps.php';

/**
 * Preloads all area-specific data for instant navigation
 * This includes: packs, tags (for all packs), and maps
 * @param {number} areaId - Area ID
 * @param {string} areaSlug - Area slug (optional, for logging)
 */
export async function preloadAreaData(areaId, areaSlug = null) {
    if (!areaId) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.warn('[Area Data Preloader] No areaId provided, skipping preload');
        }
        return;
    }

    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log(`[Area Data Preloader] Preloading data for area ${areaId}${areaSlug ? ` (${areaSlug})` : ''}`);
    }

    try {
        // 1. Trigger the centralized area batch orchestration (packs + cards)
        // This is now the single source of truth for loading area card data.
        const batchPromise = triggerAreaBatch(areaId);

        // 2. Preload maps for the area (needed for Discover)
        const mapsPromise = fetchWithCache(`${MAPS_API}?area_id=${areaId}`).catch(error => {
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.warn('[Area Data Preloader] Failed to preload maps:', error);
            }
            return [];
        });

        // 3. Preload tags (Explore Tags Cache)
        // triggerAreaTagBatch handles its own orchestration and pack fetching
        const tagsPromise = triggerAreaTagBatch(areaId);

        // 4. Preload minimal spots (Unified Minimal Cache)
        // This ensures markers are ready for both Explore and Discover
        const minimalPromise = preloadMinimalForArea(areaId);

        // Wait for essential data (but don't block entire app if one fails)
        await Promise.allSettled([batchPromise, mapsPromise, tagsPromise, minimalPromise]);

        // Wait for maps (but don't block)
        await mapsPromise;

        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Area Data Preloader] Completed preloading for area ${areaId}`);
        }
    } catch (error) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.error('[Area Data Preloader] Error preloading area data:', error);
        }
    }
}

/**
 * Preloads areas list (for menu) - should be called early in app initialization
 */
export async function preloadAreasList() {
    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[Area Data Preloader] Preloading areas list for menu');
    }

    try {
        // This will be cached by fetchWithCache
        await fetchWithCache(DATA_PATHS.EXPLORE_AREAS);

        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log('[Area Data Preloader] Areas list preloaded');
        }
    } catch (error) {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.warn('[Area Data Preloader] Failed to preload areas list:', error);
        }
    }
}

